import { useCallback } from 'react';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useShowNotification } from 'js/components/common/Notifications';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';
import { useRequestTwoFactorAuthentication } from 'js/components/popups/TwoFactorAuthenticationPopup';

import * as Types from '../../types';
import { changePassword } from './server';

export const useChangePassword = () => {
    const translate = useComponentTranslations();
    const requestTwoFactorAuthentication = useRequestTwoFactorAuthentication();

    const showNotification = useShowNotification({
        iconType: 'info',
        message: translate('successMessage')
    });
    const getErrorMessage = useGetErrorMessage();

    const submit = useCallback(async (
        { data }: { data: Types.IChangePasswordRequestData }
    ): Promise<string | void> => {
        return await new Promise((resolve, reject) => {
            const process = async (): Promise<void> => {
                try {
                    await changePassword(data);
                    showNotification();
                    resolve();
                } catch (error: Types.TChangePasswordError) {
                    const { data: { errorCode = 0 } = {} } = error?.response || error || {};

                    if (errorCode === 303) {
                        try {
                            await requestTwoFactorAuthentication({
                                onSuccess: () => process(),
                                onCancel: () => {
                                    publicEventsAPIService.trigger('2fa-popup-closed', 'change-password');
                                    resolve();
                                }
                            });
                        } catch {
                            // player has canceled the process
                        }
                    } else {
                        reject(error);
                    }
                }
            };

            process();
        });
    }, [
        showNotification,
        requestTwoFactorAuthentication
    ]);

    return useCallback(async ({ data }: { data: Types.IChangePasswordRequestData }): Promise<string | void> => {
        const { newPassword, oldPassword } = data;

        try {
            await submit({ data: { newPassword, oldPassword } });
        } catch (error) {
            return getErrorMessage(error);
        }
    }, [submit, getErrorMessage]);
};
